/**
 * AstorPrefToggle — label + sub-text + MUI Switch row used inside the
 * "How Astor works for you" card. Auto-save semantics: the parent calls the
 * preferences mutation in `onChange`; the row itself is stateless.
 */

import { Box, GlassSwitch, Typography } from '@ops-matrix/design-system-ui';
import type { ReactElement, ReactNode } from 'react';

export interface AstorPrefToggleProps {
  label: ReactNode;
  subText?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Hide the bottom border (last row in a list). */
  isLast?: boolean;
}

export function AstorPrefToggle({
  label,
  subText,
  checked,
  onChange,
  disabled,
  isLast,
}: AstorPrefToggleProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        minHeight: 44,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {subText && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
            {subText}
          </Typography>
        )}
      </Box>
      <GlassSwitch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

export default AstorPrefToggle;
